import React, { useLayoutEffect, useRef, useState } from 'react';
import { ImageOff, Loader2, Paperclip, X } from 'lucide-react';
import { Button, Text, useToast } from '@musa.tech/design-system';
import { usePresignedPhotoUrl, useUploadOnDemandPhoto } from '@/hooks/useAgreementsOnDemand';
import { MAX_PHOTO_SIZE_BYTES, PHOTO_CONTENT_TYPES } from '@/integrations/agreements/client';
import { useAnalytics } from '@/hooks/useAnalytics';
import { onDemandButtonLabel } from './onDemandAnalytics';
import type { OnDemandService } from './ServiceTypeToggle';

export interface OnDemandPhotoFieldProps {
  /** De qual pedido são as fotos — separa o evento de coleta do de caçamba no GA4. */
  service: OnDemandService;
  /** Título da seção. */
  title: string;
  /** Uma linha abaixo do título: para que servem as fotos. Sem ela, só o título. */
  description?: string;
  /** URLs devolvidas pelo upload — é o que vai em `photoUrls` no pedido. */
  photoUrls: string[];
  onChange: (photoUrls: string[]) => void;
  /** `true` quando começa um envio, `false` quando o último termina: o pedido espera as fotos. */
  onUploadingChange?: (uploading: boolean) => void;
  /** Mais campos da seção, depois das fotos (a coleta põe as observações aqui). */
  children?: React.ReactNode;
}

/** Por que a foto não pode ser anexada; nulo quando pode. */
function photoProblem(file: File): string | null {
  if (!(PHOTO_CONTENT_TYPES as readonly string[]).includes(file.type)) return 'Envie fotos em JPG ou PNG.';
  if (file.size > MAX_PHOTO_SIZE_BYTES) return 'A foto passa de 10 MB.';
  return null;
}

/**
 * Fotos opcionais de um pedido sob demanda (coleta ou movimentação de equipamento): cada foto
 * sobe assim que é escolhida, e a miniatura abre pelo link assinado.
 */
export const OnDemandPhotoField: React.FC<OnDemandPhotoFieldProps> = ({
  service,
  title,
  description,
  photoUrls,
  onChange,
  onUploadingChange,
  children,
}) => {
  const { toast } = useToast();
  const uploadPhoto = useUploadOnDemandPhoto();
  const { trackButtonClick } = useAnalytics();
  const photoInput = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const pendingUploads = useRef(0);

  // O envio termina renders depois de começar: lê sempre as fotos e os callbacks do render mais
  // recente — senão a segunda foto apagaria a primeira, ou o que foi digitado durante o envio.
  const latest = useRef({ photoUrls, onChange, onUploadingChange });
  useLayoutEffect(() => {
    latest.current = { photoUrls, onChange, onUploadingChange };
  });

  const emit = (next: string[]) => {
    latest.current = { ...latest.current, photoUrls: next };
    latest.current.onChange(next);
  };

  const trackUpload = (delta: 1 | -1) => {
    const wasUploading = pendingUploads.current > 0;
    pendingUploads.current += delta;
    const uploading = pendingUploads.current > 0;
    if (uploading === wasUploading) return;
    setIsUploading(uploading);
    latest.current.onUploadingChange?.(uploading);
  };

  const addPhotos = async (files: File[]) => {
    const accepted = files.filter((file) => {
      const problem = photoProblem(file);
      if (problem) toast({ variant: 'error', title: `${file.name} não foi anexada`, description: problem });
      return !problem;
    });
    if (!accepted.length) return;

    trackUpload(1);
    try {
      for (const file of accepted) {
        try {
          const url = await uploadPhoto.mutateAsync(file);
          emit([...latest.current.photoUrls, url]);
          // só a foto que de fato subiu conta
          trackButtonClick({ buttonLabel: onDemandButtonLabel(service, 'anexar_foto') });
        } catch {
          toast({
            variant: 'error',
            title: `${file.name} não foi anexada`,
            description: 'O envio falhou. Tente anexar de novo.',
          });
        }
      }
    } finally {
      trackUpload(-1);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <Text as="h3" variant="label" size="medium" className="text-neutral-900">
          {title}
        </Text>
        {description && (
          <Text variant="body" size="small" className="text-neutral-500">
            {description}
          </Text>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={photoInput}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png"
          className="hidden"
          data-testid="on-demand-photo-input"
          onChange={(e) => {
            if (e.target.files?.length) void addPhotos(Array.from(e.target.files));
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isUploading}
          onClick={() => photoInput.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Paperclip className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {isUploading ? 'Enviando foto...' : 'Anexar fotos'}
        </Button>
        <Text variant="body" size="small" className="text-neutral-500">
          {photoUrls.length} {photoUrls.length === 1 ? 'foto' : 'fotos'} · JPG ou PNG
        </Text>
      </div>

      {photoUrls.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {photoUrls.map((url) => (
            <div key={url} className="relative overflow-hidden rounded-md border border-border">
              <AttachedPhoto url={url} />
              <Button
                type="button"
                variant="secondary"
                size="icon"
                aria-label="Remover foto"
                className="absolute right-1 top-1 h-6 w-6"
                onClick={() => emit(latest.current.photoUrls.filter((u) => u !== url))}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {children}
    </section>
  );
};

/** Miniatura da foto anexada, pelo link assinado — a URL do upload dá 403 (bucket privado). */
const AttachedPhoto: React.FC<{ url: string }> = ({ url }) => {
  const { data: signedUrl, isError, isFetching, refetch } = usePresignedPhotoUrl(url);
  // A falha é do link, não da foto: guardada por link, um link novo tenta de novo.
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const retried = React.useRef(false);
  const failed = failedSrc != null && failedSrc === signedUrl;

  // Voltar à etapa depois de alguns minutos pode trazer do cache um link já vencido:
  // na primeira falha, pede um link novo antes de dar a foto como indisponível.
  const handleError = () => {
    setFailedSrc(signedUrl ?? null);
    if (!retried.current) {
      retried.current = true;
      void refetch();
    }
  };

  if (!signedUrl || (failed && isFetching)) {
    if (isError) return <UnavailablePhoto />;
    return (
      <div className="flex h-20 w-full items-center justify-center bg-neutral-100" data-testid="attached-photo-loading">
        <Loader2 className="h-4 w-4 animate-spin text-neutral-400" aria-label="Carregando foto" />
      </div>
    );
  }

  if (failed || isError) return <UnavailablePhoto />;

  return <img src={signedUrl} alt="Foto anexada" className="h-20 w-full object-cover" onError={handleError} />;
};

const UnavailablePhoto: React.FC = () => (
  <div className="flex h-20 w-full flex-col items-center justify-center gap-1 bg-neutral-100 text-neutral-500">
    <ImageOff className="h-4 w-4" aria-hidden="true" />
    <span className="text-[10px]">Imagem indisponível</span>
  </div>
);
